import { useState } from 'react';
import { Zap, Phone, Wifi, Tv, Lightbulb } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';

const COUNTRIES = [
  { code:'NG', name:'Nigeria', flag:'🇳🇬' },
  { code:'GH', name:'Ghana', flag:'🇬🇭' },
  { code:'KE', name:'Kenya', flag:'🇰🇪' },
  { code:'ZA', name:'South Africa', flag:'🇿🇦' },
  { code:'TZ', name:'Tanzania', flag:'🇹🇿' },
  { code:'UG', name:'Uganda', flag:'🇺🇬' },
];

const SERVICES = [
  { id:'airtime', icon: Phone, label:'Airtime' },
  { id:'data', icon: Wifi, label:'Data' },
  { id:'electricity', icon: Lightbulb, label:'Electricity' },
  { id:'tv', icon: Tv, label:'TV/Cable' },
];

export default function VTU() {
  const [country, setCountry] = useState('NG');
  const [service, setService] = useState('airtime');
  const [networks, setNetworks] = useState({ airtime:[], data:[], bills:[] });
  const [form, setForm] = useState({ phone:'', network:'', amount:'', plan:'', meterNumber:'', provider:'', smartcardNumber:'' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const selectCountry = async (code) => {
    setCountry(code);
    try {
      const res = await api.get(`/vtu/networks/${code}`);
      setNetworks(res.data.networks || {});
    } catch {}
  };

  useState(() => { selectCountry('NG'); }, []);

  const submit = async () => {
    setLoading(true);
    try {
      let res;
      if (service === 'airtime') res = await api.post('/vtu/airtime', { ...form, country });
      else if (service === 'data') res = await api.post('/vtu/data', { ...form, country });
      else if (service === 'electricity') res = await api.post('/vtu/electricity', { ...form, country });
      else res = await api.post('/vtu/tv', { ...form });
      setResult(res.data);
      toast.success(res.data.message);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mt-4 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#FF6B00,#F5A623)' }}>
          <Zap size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Sora' }}>Top Up & Bills</h1>
          <p className="text-xs text-brand-muted">Pay with crypto — instant delivery</p>
        </div>
      </div>

      {/* Country */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" style={{ scrollbarWidth:'none' }}>
        {COUNTRIES.map(c => (
          <button key={c.code} onClick={() => selectCountry(c.code)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm flex-shrink-0 transition-all font-medium"
            style={{ background: country===c.code?'linear-gradient(135deg,#FF6B00,#F5A623)':'#12121A', color: country===c.code?'white':'#6B6B8A', border: country===c.code?'none':'1px solid #1E1E2E' }}>
            {c.flag} {c.name}
          </button>
        ))}
      </div>

      {/* Service */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        {SERVICES.map(({ id, icon: Icon, label }) => (
          <button key={id} onClick={() => setService(id)}
            className="flex flex-col items-center gap-1.5 py-3 rounded-xl transition-all"
            style={{ background: service===id?'rgba(255,107,0,0.15)':'#12121A', border:`1px solid ${service===id?'#FF6B00':'#1E1E2E'}` }}>
            <Icon size={20} style={{ color: service===id?'#FF6B00':'#6B6B8A' }} />
            <span className="text-xs font-medium" style={{ color: service===id?'#FF6B00':'#6B6B8A' }}>{label}</span>
          </button>
        ))}
      </div>

      {result ? (
        <div className="glass rounded-2xl p-6 text-center">
          <div className="text-5xl mb-3">✅</div>
          <h3 className="font-bold text-lg mb-2" style={{ fontFamily:'Sora' }}>Success!</h3>
          <p className="text-brand-muted text-sm mb-2">{result.message}</p>
          {result.token && <p className="font-mono text-brand-orange font-bold text-lg mb-2">{result.token}</p>}
          <p className="text-xs text-brand-muted font-mono">Ref: {result.reference}</p>
          <button onClick={() => setResult(null)} className="btn-primary mt-4 px-8">New Transaction</button>
        </div>
      ) : (
        <div className="glass rounded-2xl p-4 space-y-3">
          {(service === 'airtime' || service === 'data') && (
            <>
              <select className="input-dark" value={form.network} onChange={e => setForm({...form,network:e.target.value})}>
                <option value="">Select Network</option>
                {(networks[service] || []).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="input-dark" placeholder="Phone Number" value={form.phone} onChange={e => setForm({...form,phone:e.target.value})} />
              {service === 'airtime' && (
                <input className="input-dark" type="number" placeholder="Amount (local currency)" value={form.amount} onChange={e => setForm({...form,amount:e.target.value})} />
              )}
              {service === 'data' && (
                <input className="input-dark" placeholder="Data Plan (e.g. 1GB, 5GB)" value={form.plan} onChange={e => setForm({...form,plan:e.target.value})} />
              )}
            </>
          )}
          {service === 'electricity' && (
            <>
              <select className="input-dark" value={form.provider} onChange={e => setForm({...form,provider:e.target.value})}>
                <option value="">Select Provider</option>
                {(networks.bills || []).filter(b => !b.includes('TV') && !b.includes('GOtv') && !b.includes('DSTV') && !b.includes('Startimes')).map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="input-dark" placeholder="Meter Number" value={form.meterNumber} onChange={e => setForm({...form,meterNumber:e.target.value})} />
              <input className="input-dark" type="number" placeholder="Amount" value={form.amount} onChange={e => setForm({...form,amount:e.target.value})} />
            </>
          )}
          {service === 'tv' && (
            <>
              <select className="input-dark" value={form.provider} onChange={e => setForm({...form,provider:e.target.value})}>
                <option value="">Select Provider</option>
                {['DSTV','GOtv','Startimes'].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <input className="input-dark" placeholder="Smartcard / IUC Number" value={form.smartcardNumber} onChange={e => setForm({...form,smartcardNumber:e.target.value})} />
              <input className="input-dark" placeholder="Plan (e.g. DStv Compact)" value={form.plan} onChange={e => setForm({...form,plan:e.target.value})} />
            </>
          )}
          <button onClick={submit} disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
            {loading ? <div className="spinner" /> : <><Zap size={16} /> Pay Now</>}
          </button>
        </div>
      )}
    </div>
  );
}
